/**
 * Travel VFX feature (M3 plan R10, order `FEATURE_ORDER.reachFx`, reach mode only). Each frame it reads
 * `ctx.reach.travel` and drives:
 * - the relay tunnel, at the render origin along the ship's nose, while the travel mode is `transit` (its own
 *   phase clock times the `enter` fade-in and the `exit` fade-out);
 * - the cruise streaks, in `cruise` and `lane`, along the ship's velocity;
 * - the interdiction feedback: `post.setFeedback(feedback + pull × feedbackPull, 0)`, a camera jitter of ≤ 0.6°
 *   (seeded noise on `camera.quaternion`, after the camera feature, only with screen shake on) and a brief flash
 *   on `REV.cruiseDrop`. `setFeedback` is called only while the travel mode is not `normal` or a drop flash runs,
 *   plus one zeroing call after, so M1's feedback is untouched otherwise.
 *
 * In dev builds the "Reach FX" tuning group (pushed onto `ctx.reach.tuning`, so the dev panel lists it) carries
 * preview toggles that show each effect on demand, including a relay core cycling online → offline → reboot in
 * front of the camera. `frame` allocates nothing (D-014).
 */
import { Euler, Group, Quaternion, Vector3 } from 'three/webgpu'
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { EVENT_MASK, firstReadable, type EventRing } from '../../combat/events'
import { logger } from '../../core/logger'
import { REV } from '../../reach/events'
import type { TravelMode } from '../../reach/runtime/types'
import type { TuningGroup, TuningParam } from '../../render/contracts'
import { loadReachFxConfig, type ReachFxConfig } from '../../render/reachFx/config'
import { createInterdictionFx, stepInterdictionFx, triggerDropFlash, type InterdictionFx } from '../../render/reachFx/interdiction'
import { createRelayCore, type CoreState, type RelayCore } from '../../render/reachFx/relayCore'
import { createCruiseStreaks, type CruiseStreaks } from '../../render/reachFx/streaks'
import { createTunnel, type Tunnel, type TunnelPhase } from '../../render/reachFx/tunnel'
import { Rot, Vel } from '../../world/components'

/** `REV.cruiseDrop` reason (the event's `b`) for a drop out of a failed interdiction. */
const DROP_INTERDICTED = 3
/** Below this speed (m/s) the velocity has no usable direction: the streaks follow the camera's forward. */
const MIN_DIR_SPEED = 1e-3
/** Preview relay-core states, in cycle order: online, offline, reboot. */
const PREVIEW_CORE_STATES: readonly CoreState[] = [1, 0, 2]

/** Dev-only preview toggles (the dev panel binds them through the tuning group). */
export interface ReachFxPreview {
  tunnel: boolean
  streaks: boolean
  interdiction: boolean
  core: boolean
}

export interface ReachFxState {
  readonly tunnel: Tunnel
  readonly streaks: CruiseStreaks
  readonly fx: InterdictionFx
  readonly preview: ReachFxPreview
  /** The dev preview relay core (dev builds only). */
  readonly core: RelayCore | null
  cursor: number
  phase: TunnelPhase
  /** Seconds in the current tunnel phase (frame time). */
  phaseClock: number
  /** Seconds since the preview toggles last changed (drives the preview cycles). */
  previewClock: number
}

export interface ReachFxFeature extends GameFeature {
  readonly config: ReachFxConfig
  /** Null outside reach mode and before init. */
  readonly state: ReachFxState | null
  readonly tuning: TuningGroup | null
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

export interface ReachFxOptions {
  /** Compile the effect pipelines at init (default true; tests pass false). */
  warmUp?: boolean
  /** Adds the preview toggles and the preview relay core (default: `import.meta.env.DEV`). */
  dev?: boolean
}

// Frame scratch (D-014).
const scratchDir = new Vector3()
const scratchEuler = new Euler(0, 0, 0, 'YXZ')
const scratchQuat = new Quaternion()

/** Starts a drop flash for every `REV.cruiseDrop` since the cursor, then moves the cursor to the head. */
export function readDropEvents(s: ReachFxState, ring: EventRing, cfg: ReachFxConfig): void {
  for (let i = firstReadable(ring, s.cursor); i < ring.head; i++) {
    const slot = i & EVENT_MASK
    if (ring.type[slot] === REV.cruiseDrop) triggerDropFlash(s.fx, ring.other[slot] === DROP_INTERDICTED, cfg.interdiction)
  }
  s.cursor = ring.head
}

/** The tunnel: phase from the travel state (or the preview), eased by the feature's own phase clock. */
export function updateTunnel(ctx: GameContext, s: ReachFxState, cfg: ReachFxConfig, mode: TravelMode, transitPhase: TunnelPhase, dt: number): void {
  let phase: TunnelPhase = mode === 'transit' ? transitPhase : 'none'
  if (phase === 'none' && s.preview.tunnel) phase = 'tunnel'
  if (phase !== s.phase) {
    s.phase = phase
    s.phaseClock = 0
  } else s.phaseClock += dt
  let t01 = 1
  if (phase === 'enter') t01 = s.phaseClock / cfg.tunnel.fadeInS
  else if (phase === 'exit') t01 = s.phaseClock / cfg.tunnel.fadeOutS
  s.tunnel.setPhase(phase, t01)
  if (!s.tunnel.root.visible) return
  const p = ctx.sim.playerEid
  if (p >= 0) s.tunnel.root.quaternion.set(Rot.x[p], Rot.y[p], Rot.z[p], Rot.w[p])
  else s.tunnel.root.quaternion.copy(ctx.camera.quaternion)
  s.tunnel.update(dt)
}

/** The cruise streaks, along the player's velocity (or the camera's forward when there is none). */
export function updateStreaks(ctx: GameContext, s: ReachFxState, cfg: ReachFxConfig, mode: TravelMode, speed: number, dt: number): void {
  const preview = s.preview.streaks && mode !== 'cruise' && mode !== 'lane'
  const show = preview || mode === 'cruise' || mode === 'lane'
  s.streaks.root.visible = show
  if (!show) return
  const p = ctx.sim.playerEid
  let vx = 0
  let vy = 0
  let vz = 0
  if (p >= 0) {
    vx = Vel.x[p]
    vy = Vel.y[p]
    vz = Vel.z[p]
  }
  const v = Math.sqrt(vx * vx + vy * vy + vz * vz)
  if (v > MIN_DIR_SPEED && !preview) scratchDir.set(vx / v, vy / v, vz / v)
  else scratchDir.set(0, 0, -1).applyQuaternion(ctx.camera.quaternion)
  s.streaks.update(dt, preview ? cfg.devPreview.streakSpeed : speed, scratchDir.x, scratchDir.y, scratchDir.z)
}

/** Interdiction feedback and jitter, and the drop flash. */
export function updateInterdiction(ctx: GameContext, s: ReachFxState, cfg: ReachFxConfig, mode: TravelMode, pull: number, dt: number, paused: boolean): void {
  let m = mode
  let pl = pull
  if (s.preview.interdiction && mode === 'normal') {
    m = 'interdicted'
    pl = 0.5 + 0.5 * Math.sin(s.previewClock * 0.5)
  }
  if (stepInterdictionFx(s.fx, m, pl, dt, cfg.interdiction)) ctx.post.setFeedback(s.fx.feedback, 0)
  if (paused || !ctx.settings.screenShake) return
  if (s.fx.pitch === 0 && s.fx.yaw === 0 && s.fx.roll === 0) return
  scratchEuler.set(s.fx.pitch, s.fx.yaw, s.fx.roll, 'YXZ')
  scratchQuat.setFromEuler(scratchEuler)
  ctx.camera.quaternion.multiply(scratchQuat)
}

/** The dev preview relay core: in front of the camera, cycling online → offline → reboot (real time, even paused). */
export function updatePreviewCore(ctx: GameContext, s: ReachFxState, cfg: ReachFxConfig, dt: number): void {
  const core = s.core
  if (!core) return
  core.mesh.visible = s.preview.core
  if (!s.preview.core) return
  const period = cfg.devPreview.coreStateS
  const cycle = s.previewClock / period
  const index = Math.floor(cycle) % PREVIEW_CORE_STATES.length
  core.setState(PREVIEW_CORE_STATES[index], cycle - Math.floor(cycle))
  scratchDir.set(0, 0, -cfg.devPreview.coreDistanceM).applyQuaternion(ctx.camera.quaternion)
  core.mesh.position.copy(scratchDir)
  core.mesh.quaternion.copy(ctx.camera.quaternion)
  core.update(dt)
}

/** Per-frame work (allocation-free, D-014). */
export function frameReachFx(ctx: GameContext, s: ReachFxState, cfg: ReachFxConfig, frameDt: number, paused: boolean): void {
  const reach = ctx.reach
  if (!reach) return
  const travel = reach.travel
  const dt = paused ? 0 : frameDt
  readDropEvents(s, reach.events, cfg)
  // The dev previews run on real time, so they can be inspected from the pause menu.
  s.previewClock += frameDt
  // The camera jitter applies after the tunnel and streaks read the un-jittered camera.
  updateTunnel(ctx, s, cfg, travel.mode, travel.transit.phase, dt)
  updateStreaks(ctx, s, cfg, travel.mode, travel.speed, dt)
  updatePreviewCore(ctx, s, cfg, frameDt)
  updateInterdiction(ctx, s, cfg, travel.mode, travel.interdiction.pull, dt, paused)
}

function numberParam(p: TuningParam | undefined, fallback: number): number {
  return p && typeof p.value === 'number' ? p.value : fallback
}

function boolParam(p: TuningParam | undefined): boolean {
  return p !== undefined && p.value === true
}

/** The "Reach FX" dev-panel group: the main looks, plus the preview toggles in dev builds. */
function createTuning(cfg: ReachFxConfig, s: ReachFxState, dev: boolean): TuningGroup {
  const params: Record<string, TuningParam> = {
    tunnelStreakIntensity: { value: cfg.tunnel.streakIntensity, min: 0, max: 30, step: 0.1 },
    tunnelStreakScroll: { value: cfg.tunnel.streakScroll, min: 0, max: 20, step: 0.1 },
    tunnelMaxOpacity: { value: cfg.tunnel.maxOpacity, min: 0, max: 1, step: 0.01 },
    streakMaxAlpha: { value: cfg.streaks.maxAlpha, min: 0, max: 1, step: 0.01 },
    streakIntensity: { value: cfg.streaks.intensity, min: 0, max: 20, step: 0.1 },
    streakWidthM: { value: cfg.streaks.widthM, min: 0.05, max: 3, step: 0.05 },
    interdictionFeedbackPull: { value: cfg.interdiction.feedbackPull, min: 0, max: 1, step: 0.01 },
    interdictionJitterMaxDeg: { value: cfg.interdiction.jitterMaxDeg, min: 0, max: 0.6, step: 0.01 },
    relayOnlineIntensity: { value: cfg.relayCore.onlineIntensity, min: 0, max: 20, step: 0.1 },
  }
  if (dev) {
    params.previewTunnel = { value: false }
    params.previewStreaks = { value: false }
    params.previewInterdiction = { value: false }
    params.previewCore = { value: false }
  }
  return {
    name: 'Reach FX',
    params,
    apply(): void {
      cfg.tunnel.streakIntensity = numberParam(params.tunnelStreakIntensity, cfg.tunnel.streakIntensity)
      cfg.tunnel.streakScroll = numberParam(params.tunnelStreakScroll, cfg.tunnel.streakScroll)
      cfg.tunnel.maxOpacity = numberParam(params.tunnelMaxOpacity, cfg.tunnel.maxOpacity)
      cfg.streaks.maxAlpha = numberParam(params.streakMaxAlpha, cfg.streaks.maxAlpha)
      cfg.streaks.intensity = numberParam(params.streakIntensity, cfg.streaks.intensity)
      cfg.streaks.widthM = numberParam(params.streakWidthM, cfg.streaks.widthM)
      cfg.interdiction.feedbackPull = numberParam(params.interdictionFeedbackPull, cfg.interdiction.feedbackPull)
      cfg.interdiction.jitterMaxDeg = Math.min(0.6, numberParam(params.interdictionJitterMaxDeg, cfg.interdiction.jitterMaxDeg))
      cfg.relayCore.onlineIntensity = numberParam(params.relayOnlineIntensity, cfg.relayCore.onlineIntensity)
      s.tunnel.refresh()
      s.streaks.refresh()
      s.core?.refresh()
      if (!dev) return
      const next: ReachFxPreview = {
        tunnel: boolParam(params.previewTunnel),
        streaks: boolParam(params.previewStreaks),
        interdiction: boolParam(params.previewInterdiction),
        core: boolParam(params.previewCore),
      }
      const p = s.preview
      if (next.tunnel !== p.tunnel || next.streaks !== p.streaks || next.interdiction !== p.interdiction || next.core !== p.core) {
        s.previewClock = 0
      }
      Object.assign(p, next)
    },
  }
}

/** Compiles the tunnel and streak pipelines at load, so the first jump never stalls on a shader compile. */
async function warmPipelines(ctx: GameContext, s: ReachFxState): Promise<void> {
  const holder = new Group()
  s.tunnel.root.visible = true
  s.streaks.root.visible = true
  holder.add(s.tunnel.root, s.streaks.root)
  if (s.core) holder.add(s.core.mesh)
  try {
    await ctx.renderer.compileAsync(holder, ctx.camera, ctx.scene)
  } catch (err: unknown) {
    logger.warn('reachFx: pipeline warm-up failed; the effects compile on first use', err)
  } finally {
    s.tunnel.root.visible = false
    s.streaks.root.visible = false
    ctx.scene.add(s.tunnel.root, s.streaks.root)
    if (s.core) {
      s.core.mesh.visible = false
      ctx.scene.add(s.core.mesh)
    }
  }
}

export function createReachFxFeature(config: ReachFxConfig = loadReachFxConfig(), options: ReachFxOptions = {}): ReachFxFeature {
  let state: ReachFxState | null = null
  let tuning: TuningGroup | null = null
  const dev = options.dev ?? import.meta.env.DEV

  const feature: ReachFxFeature = {
    name: 'reachFx',
    order: FEATURE_ORDER.reachFx,
    config,
    get state(): ReachFxState | null {
      return state
    },
    get tuning(): TuningGroup | null {
      return tuning
    },
    async init(ctx: GameContext): Promise<void> {
      const reach = ctx.reach
      if (!reach) return
      const s: ReachFxState = {
        tunnel: createTunnel(config.tunnel),
        streaks: createCruiseStreaks(config.streaks),
        fx: createInterdictionFx(config.interdiction),
        preview: { tunnel: false, streaks: false, interdiction: false, core: false },
        core: dev ? createRelayCore(config.devPreview.coreApertureM, config.relayCore) : null,
        cursor: reach.events.head,
        phase: 'none',
        phaseClock: 0,
        previewClock: 0,
      }
      state = s
      tuning = createTuning(config, s, dev)
      reach.tuning.push(tuning)
      if (options.warmUp ?? true) await warmPipelines(ctx, s)
      else {
        ctx.scene.add(s.tunnel.root, s.streaks.root)
        if (s.core) {
          s.core.mesh.visible = false
          ctx.scene.add(s.core.mesh)
        }
      }
    },
    frame(ctx: GameContext, f: FrameInfo): void {
      const s = state
      if (!s) return
      frameReachFx(ctx, s, config, f.dt, f.paused)
    },
    dispose(): void {
      const s = state
      if (!s) return
      s.tunnel.dispose()
      s.streaks.dispose()
      s.core?.dispose()
      state = null
      tuning = null
    },
  }
  return feature
}

const feature: ReachFxFeature = createReachFxFeature()
export default feature
